import { useCallback, useEffect, useRef } from "react";
import {
  DEFAULT_DEBOUNCE_MS,
  MIN_CHARS_FOR_SCAN
} from "../constants/barcodeScanner";

/**
 * useBarcodeScanner
 * Captures input from a barcode gun. Barcode guns typically type characters
 * rapidly followed by Enter. This hook distinguishes scan input from manual typing.
 *
 * @param onScan - Callback when a barcode is detected (receives the scanned value)
 * @param options - { debounceMs, minChars }
 */
export const useBarcodeScanner = (
  onScan: (value: string) => void,
  options?: {
    debounceMs?: number;
    minChars?: number;
    enabled?: boolean;
  }
) => {
  const {
    debounceMs = DEFAULT_DEBOUNCE_MS,
    minChars = MIN_CHARS_FOR_SCAN,
    enabled = true
  } = options ?? {};

  const bufferRef = useRef("");
  const lastKeyTimeRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushBuffer = useCallback(() => {
    const value = bufferRef.current.trim();
    if (value.length >= minChars) {
      onScan(value);
    }
    bufferRef.current = "";
  }, [minChars, onScan]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enabled) return;

      // Ignore keystrokes when user is typing in a form field (prevents form input
      // e.g. dates like "2024-12-31" from being treated as a barcode scan)
      const target = e.target as Node | null;
      if (target && typeof (target as HTMLElement).tagName === "string") {
        const tag = (target as HTMLElement).tagName.toUpperCase();
        const editable =
          (target as HTMLElement).getAttribute?.("contenteditable") === "true";
        if (
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          editable
        ) {
          return;
        }
      }

      const now = Date.now();
      const timeSinceLastKey = now - lastKeyTimeRef.current;

      const isEnter =
        e.key === "Enter" ||
        (e as KeyboardEvent & { keyCode?: number }).keyCode === 13 ||
        (e as KeyboardEvent & { keyCode?: number }).keyCode === 108;
      if (isEnter) {
        e.preventDefault();
        if (bufferRef.current.trim().length >= minChars) {
          flushBuffer();
        } else {
          bufferRef.current = "";
        }
        lastKeyTimeRef.current = now;
        return;
      }

      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (timeSinceLastKey > debounceMs && bufferRef.current.length > 0) {
          bufferRef.current = "";
        }
        bufferRef.current += e.key;
        lastKeyTimeRef.current = now;

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          bufferRef.current = "";
          timeoutRef.current = null;
        }, debounceMs);
      }
    },
    [enabled, debounceMs, minChars, flushBuffer]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [handleKeyDown]);
};
